import { useEffect, useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/** Standard document page chrome: header + body + sticky footer. */
export function DocumentPage({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("relative mx-auto max-w-6xl space-y-2 pb-20", className)}>{children}</div>
  );
}

export function DocumentHeader({
  title,
  subtitle,
  back,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1.5">
        {back}
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold tracking-[-0.01em]">{title}</h1>
          {subtitle ? <p className="truncate text-2xs text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
    </header>
  );
}

/** Sticky totals + primary actions for document entry. */
export function DocumentStickyBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 px-3 py-1.5 backdrop-blur",
        "md:left-[var(--sidebar-width,14rem)]",
        className,
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2">{children}</div>
    </div>
  );
}

/** Progressive disclosure for notes / terms / advanced fields. */
export function CollapsibleBlock({
  title,
  defaultOpen = false,
  children,
  className,
  aside,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  aside?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className={cn("border-b border-border/80", className)}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 py-1.5 text-left"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs font-semibold">{title}</span>
        <span className="flex items-center gap-2">
          {aside}
          <ChevronDown
            className={cn("size-3.5 text-muted-foreground transition-transform", open && "rotate-180")}
          />
        </span>
      </button>
      {open ? (
        <div id={panelId} className="pb-2">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** Ctrl/Cmd+S and Ctrl/Cmd+Enter → save. */
export function useDocumentSaveShortcut(onSave: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "s" || (e.key === "Enter" && mod)) {
        e.preventDefault();
        onSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSave, enabled]);
}
